import { MARKET_DATA_RESOLVE_DIALOG_COPY } from "../constants/marketDataResolveDialogCopy";
import { accountSettingsTabPath } from "../constants/accountSettingsSidebar";
import { buildConnectBrokerManagementOpenUrl } from "../views/Dashboard/Profile/connectBroker/connectBrokerWizardConstants";
import { brokerRequiresMdFetchConsent } from "../components/MarketData/MarketDataFetchConsentCheckbox";
import type { MarketDataConnectOption } from "../types/marketDataAttribution";
import type { MarketDataSourceOption } from "../types/marketDataSources";
import type { AvailableInstrumentRow } from "../types/tradeManagementReport";
import type { ResolveInstrumentResponse } from "../types/instruments";
import {
  defaultMarketDataSourceOption,
  formatMarketDataSourceUnavailableReason,
  hasSavedMarketDataSourcePreference,
  humanizeMarketDataBrokerSlug,
  marketDataBrokerKey,
  resolveMarketDataSourceOptions,
} from "./marketDataSourceChoice";
import {
  instrumentChartsBlocked,
  instrumentNeedsSetup,
  resolveInstrumentRowForScan,
  type InstrumentMarketDataRow,
  type InstrumentSetupAccount,
} from "./instrumentMarketDataReadiness";
import {
  isMarketDataConnectGateDetail,
  isMarketDataPreferenceUnavailableDetail,
  parseMarketDataUnresolvedDetail,
} from "./marketDataResolution";

export { isMarketDataConnectGateDetail, isMarketDataPreferenceUnavailableDetail };

const CENTRAL_MARKET_DATA_BROKERS = new Set([
  "twelvedata",
  "coinapi",
  "databento",
  "binance",
  "ibkr",
  "hyperliquid",
]);

/** True when the user should reconnect a broker account (BYOK feed). */
export function isBrokerConnectMarketDataOption(broker: string | undefined | null): boolean {
  const normalized = (broker ?? "").trim().toLowerCase();
  if (!normalized) return false;
  if (brokerRequiresMdFetchConsent(normalized)) return true;
  return !CENTRAL_MARKET_DATA_BROKERS.has(normalized);
}

export function buildMarketDataConnectOptionHref(broker: string): string {
  if (isBrokerConnectMarketDataOption(broker)) {
    return buildConnectBrokerManagementOpenUrl({ brokerName: broker });
  }
  return accountSettingsTabPath("instrument-market-data");
}

export type MarketDataSourceConnectAction = {
  label: string;
  broker: string;
  href: string;
};

/** Connect CTA when BE marks a switchable OHLC lane unavailable (manage / reconfigure UI). */
export function buildMarketDataSourceConnectAction(
  source: MarketDataSourceOption | null | undefined
): MarketDataSourceConnectAction | null {
  if (!source || source.available !== false) return null;

  const reason = (source.unavailable_reason ?? "").trim().toLowerCase();
  if (
    reason !== "broker_not_connected" &&
    reason !== "market_data_consent_required"
  ) {
    return null;
  }

  const broker = marketDataBrokerKey(source);
  if (!broker || !isBrokerConnectMarketDataOption(broker)) return null;

  const display =
    source.broker_display?.trim() ||
    source.market_data_broker_display?.trim() ||
    humanizeMarketDataBrokerSlug(broker) ||
    "broker";

  return {
    label: `Connect ${display}`,
    broker,
    href: buildMarketDataConnectOptionHref(broker),
  };
}

export type MarketDataConnectGateState = {
  message: string;
  action?: string;
  connect_options: MarketDataConnectOption[];
  trade_analytics_available?: boolean;
  /** Row symbol when gate came from instrument-setup scan (wizard focus). */
  symbol?: string;
};

const SAVED_SOURCE_UNAVAILABLE_REASONS = new Set([
  "broker_not_connected",
  "market_data_consent_required",
]);

function brokerConnectOptionFromSource(
  source: MarketDataSourceOption
): MarketDataConnectOption | null {
  const broker = marketDataBrokerKey(source);
  if (!broker || !isBrokerConnectMarketDataOption(broker)) return null;
  const display =
    source.broker_display?.trim() ||
    source.market_data_broker_display?.trim() ||
    humanizeMarketDataBrokerSlug(broker) ||
    "broker";
  return {
    broker,
    label: `Connect ${display}`,
  };
}

function savedMarketDataSourceForRow(input: {
  md_listing_key?: string | null;
  market_data_broker?: string | null;
  saved_market_data_broker?: string | null;
  selected_market_data_source?: MarketDataSourceOption | null;
  market_data_sources?: MarketDataSourceOption[];
  candidate_brokers?: Parameters<typeof resolveMarketDataSourceOptions>[0]["candidate_brokers"];
}): MarketDataSourceOption | null {
  const sources = resolveMarketDataSourceOptions(input);
  const selected =
    sources.find((source) => source.is_selected) ?? defaultMarketDataSourceOption(sources);
  if (selected) return selected;

  if (input.selected_market_data_source) return input.selected_market_data_source;

  const listingKey = input.md_listing_key?.trim();
  const broker = input.saved_market_data_broker?.trim() || input.market_data_broker?.trim();
  if (!listingKey && !broker) return null;

  return {
    md_listing_key: listingKey,
    market_data_broker: broker,
    broker,
  };
}

/** Proactive connect gate when a saved OHLC listing exists but its broker feed is unavailable. */
export function marketDataConnectGateFromInstrumentRow(
  row:
    | {
        symbol?: string;
        md_listing_key?: string | null;
        market_data_broker?: string | null;
        saved_market_data_broker?: string | null;
        selected_market_data_source?: MarketDataSourceOption | null;
        market_data_sources?: MarketDataSourceOption[];
        candidate_brokers?: Parameters<typeof resolveMarketDataSourceOptions>[0]["candidate_brokers"];
      }
    | null
    | undefined
): MarketDataConnectGateState | null {
  if (!row) return null;

  const hasSavedListing =
    hasSavedMarketDataSourcePreference(row) || Boolean(row.md_listing_key?.trim());
  if (!hasSavedListing) return null;

  const savedSource = savedMarketDataSourceForRow(row);
  if (!savedSource || savedSource.available !== false) return null;

  const reason = (savedSource.unavailable_reason ?? "").trim().toLowerCase();
  if (!SAVED_SOURCE_UNAVAILABLE_REASONS.has(reason)) return null;

  const connectOption = brokerConnectOptionFromSource(savedSource);
  const providerLabel =
    savedSource.broker_display?.trim() ||
    savedSource.market_data_broker_display?.trim() ||
    (connectOption?.broker
      ? humanizeMarketDataBrokerSlug(connectOption.broker)
      : undefined) ||
    "your saved chart data source";

  return {
    message: `Your saved chart data source (${providerLabel}) is not connected. Reconnect your broker or switch to another market data source.`,
    action:
      formatMarketDataSourceUnavailableReason(reason) ??
      "Fix the fields shown and try again.",
    connect_options: connectOption ? [connectOption] : [],
    trade_analytics_available: true,
  };
}

/**
 * Inline OHLC/sim blocked notice — prefers connect gate from source metadata, then
 * authoritative row `chart_available` / `setup_complete` (GET setup may omit source.available).
 */
/** Layer B gate from instrument-setup scan (symbol match or single-symbol account). */
export function marketDataOhlcGateFromSetupScan(
  scan: InstrumentSetupAccount | null | undefined,
  symbol?: string | null
): MarketDataConnectGateState | null {
  const row = resolveInstrumentRowForScan(scan, symbol);
  const gate = marketDataOhlcBlockedNoticeFromInstrumentRow(row);
  if (!gate) return null;
  const rowSymbol = row?.symbol?.trim();
  return rowSymbol ? { ...gate, symbol: rowSymbol } : gate;
}

export function marketDataOhlcBlockedNoticeFromInstrumentRow(
  row: InstrumentMarketDataRow | null | undefined
): MarketDataConnectGateState | null {
  if (!row || row.setup_skipped) return null;

  const connectGate = marketDataConnectGateFromInstrumentRow(row);
  if (connectGate) return connectGate;

  if (!instrumentChartsBlocked(row)) return null;

  if (instrumentNeedsSetup(row)) {
    return {
      message:
        "Confirm market data for this symbol before running simulations or OHLC charts.",
      action: row.blocking_reason
        ? `Still needed: ${row.blocking_reason.replace(/_/g, " ")}`
        : undefined,
      connect_options: [],
      trade_analytics_available: row.trade_analytics_available ?? true,
    };
  }

  const providerLabel =
    row.saved_market_data_broker_display?.trim() ||
    row.market_data_broker_display?.trim() ||
    humanizeMarketDataBrokerSlug(row.saved_market_data_broker ?? undefined) ||
    "your saved chart data source";

  const connectOptions: MarketDataConnectOption[] = [];
  const seenBrokers = new Set<string>();
  for (const source of row.market_data_sources ?? []) {
    const option = brokerConnectOptionFromSource(source);
    if (!option || source.available !== false) continue;
    const key = option.broker.trim().toLowerCase();
    if (!key || seenBrokers.has(key)) continue;
    seenBrokers.add(key);
    connectOptions.push(option);
  }

  const reason = (row.chart_unavailable_reason ?? "market_data_preference_unavailable")
    .trim()
    .toLowerCase();

  return {
    message: `Your saved chart data source (${providerLabel}) is not connected. Reconnect your broker or switch to another market data source.`,
    action:
      formatMarketDataSourceUnavailableReason(reason) ??
      "Reconnect your broker or switch to another OHLC source.",
    connect_options: connectOptions,
    trade_analytics_available: row.trade_analytics_available ?? true,
  };
}

export function isMarketDataConnectGateBlocked(
  gate: MarketDataConnectGateState | null | undefined
): boolean {
  return Boolean(gate);
}

export const MARKET_DATA_CONNECT_GATE_BLOCKED_MESSAGE =
  "Reconnect your saved chart data source or switch to another OHLC listing before generating.";

export type MarketDataConnectorRequiredGate = {
  symbol: string;
  asset_class?: string;
  message?: string;
  connect_options: MarketDataConnectOption[];
  market_data_sources?: MarketDataSourceOption[];
};

export function marketDataConnectGateFromConnectorRequired(
  pending: MarketDataConnectorRequiredGate | null | undefined
): MarketDataConnectGateState | null {
  if (!pending) return null;
  return {
    message:
      pending.message?.trim() ||
      "Your saved chart data source is not connected. Reconnect your broker or switch to another market data source.",
    connect_options: pending.connect_options ?? [],
    trade_analytics_available: true,
  };
}

export function marketDataConnectGateFromResolveResult(
  result: ResolveInstrumentResponse | null | undefined
): MarketDataConnectGateState | null {
  if (!result) return null;
  return marketDataConnectGateFromApiError(result);
}

/**
 * Report wizard rows often omit `market_data_sources[].available`. Re-resolve when the
 * saved listing uses a BYOK broker so POST /instruments/resolve can surface disconnect.
 */
/** BYOK listing selected but POST /instruments/resolve has not confirmed availability yet. */
export function isMarketDataAvailabilityVerificationPending(input: {
  row: AvailableInstrumentRow | null | undefined;
  symbol: string;
  verifiedSymbol: string | null;
  resolveLoading: boolean;
}): boolean {
  const tradeSymbol = input.symbol.trim();
  if (!tradeSymbol) return false;
  if (!needsMarketDataAvailabilityResolveForRow(input.row)) return false;
  if (input.resolveLoading) return true;
  return input.verifiedSymbol !== tradeSymbol;
}

export function needsMarketDataAvailabilityResolveForRow(
  row: AvailableInstrumentRow | null | undefined
): boolean {
  if (!row || row.md_supported === false) return false;

  const hasSavedListing =
    hasSavedMarketDataSourcePreference(row) || Boolean(row.md_listing_key?.trim());
  if (!hasSavedListing) return false;

  if (marketDataConnectGateFromInstrumentRow(row)) return false;

  const savedBroker =
    row.saved_market_data_broker?.trim() ||
    row.market_data_broker?.trim() ||
    marketDataBrokerKey(savedMarketDataSourceForRow(row) ?? {});

  if (isBrokerConnectMarketDataOption(savedBroker)) return true;

  const sources = resolveMarketDataSourceOptions(row);
  const selected =
    sources.find((source) => source.is_selected) ??
    defaultMarketDataSourceOption(sources);
  if (!selected || selected.available === false) return false;

  return isBrokerConnectMarketDataOption(marketDataBrokerKey(selected));
}


export type MarketDataConnectGateAction =
  | { kind: "connect"; label: string; broker: string }
  | { kind: "switch_source"; label: string };

/** Broker reconnect + a generic path to market data preference settings. */
export function buildMarketDataConnectGateActions(
  connectOptions: MarketDataConnectOption[]
): MarketDataConnectGateAction[] {
  const seen = new Set<string>();
  const actions: MarketDataConnectGateAction[] = [];

  for (const option of connectOptions) {
    if (!isBrokerConnectMarketDataOption(option.broker)) continue;
    const broker = option.broker.trim().toLowerCase();
    if (!broker || seen.has(broker)) continue;
    seen.add(broker);
    actions.push({
      kind: "connect",
      label: option.label?.trim() || "Connect broker",
      broker: option.broker,
    });
  }

  actions.push({ kind: "switch_source", label: "Use another data source" });
  return actions;
}

export function marketDataConnectGateFromApiError(
  value: unknown
): MarketDataConnectGateState | null {
  const detail = parseMarketDataUnresolvedDetail(value);
  if (!detail || !isMarketDataConnectGateDetail(detail)) return null;

  const message =
    detail.message?.trim() ||
    "Charts need a connected market data source. Reconnect your broker or switch to another source.";

  return {
    message,
    action: detail.action,
    connect_options: detail.connect_options ?? [],
    trade_analytics_available: detail.trade_analytics_available,
  };
}

export const MARKET_DATA_RESOLVE_SOURCE_CTA_LABEL =
  MARKET_DATA_RESOLVE_DIALOG_COPY.confirmMarketDataSource;

export const MARKET_DATA_PREFERENCE_SETTINGS_PATH = accountSettingsTabPath(
  "instrument-market-data"
);
